import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getAsientosOcupados } from '@/services/api/new.funciones.service';

// Query key factory para asientos ocupados
export const occupiedSeatsKeys = {
    all: ['occupied-seats'] as const,
    byFuncion: (funcionId: string) => [...occupiedSeatsKeys.all, funcionId] as const,
};

/**
 * Hook para obtener asientos ocupados con TanStack Query.
 * - Cache automático con ttl de 10s
 * - Garbage collection a los 30s
 * - Deduplicación de requests automática
 * - Background refetch cuando los datos están stale
 */
export function useOccupiedSeats(funcionId: string | null | undefined) {
    const { data, isLoading, isFetching } = useQuery({
        queryKey: occupiedSeatsKeys.byFuncion(funcionId!),
        queryFn: () => getAsientosOcupados(funcionId!),
        enabled: !!funcionId,
        staleTime: 10 * 1000,
        gcTime: 30 * 1000,
    });

    return {
        seats: data?.ocupados ?? [],
        blockedSeats: data?.bloqueados ?? [],
        loading: isLoading,
        refreshing: isFetching && !isLoading,
    };
}

/**
 * Invalida el cache para una función específica y dispara refetch.
 */
export function useInvalidateOccupiedSeats() {
    const queryClient = useQueryClient();

    return (funcionId: string) => {
        queryClient.invalidateQueries({
            queryKey: occupiedSeatsKeys.byFuncion(funcionId)
        });
    };
}

/**
 * Limpia todo el cache de asientos ocupados.
 */
export function useClearOccupiedSeats() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({
            queryKey: occupiedSeatsKeys.all
        });
    };
}

/**
 * Prefetchea los asientos ocupados de múltiples funciones en background.
 * Ideal para llamar cuando se carga un evento con varias funciones.
 */
export function usePrefetchOccupiedSeats(funcionIds: string[]) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!funcionIds.length) return;

        Promise.all(
            funcionIds.map((funcionId) =>
                queryClient.prefetchQuery({
                    queryKey: occupiedSeatsKeys.byFuncion(funcionId),
                    queryFn: () => getAsientosOcupados(funcionId),
                    staleTime: 10 * 1000,
                })
            )
        );
    }, [funcionIds, queryClient]);
}
